import { Link, useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { getLocaleFromPath, withLocalePath } from '@/lib/i18n-utils'

/** A section header: title left, a figure or note right, over a strong rule. */
export function LedgerSection({
  title,
  note,
  children,
  id,
}: {
  title: string
  note?: string
  children: ReactNode
  id?: string
}) {
  return (
    <section id={id} className="ledger-section ledger-wrap">
      <div className="ledger-section-head">
        <h2 className="ledger-section-title">{title}</h2>
        {note ? <span className="ledger-section-count">{note}</span> : null}
      </div>
      {children}
    </section>
  )
}

/** A ruled register row: number, name, description, tag. */
export function LedgerRow({
  index,
  name,
  desc,
  tag,
  href,
}: {
  index: string
  name: string
  desc: string
  tag?: string
  href?: string
}) {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  const inner = (
    <>
      <span className="ledger-register-no">{index}</span>
      <h3 className="ledger-register-name">{name}</h3>
      <p className="ledger-register-desc">{desc}</p>
      {tag ? <span className="ledger-register-tag">{tag}</span> : <span />}
    </>
  )
  if (href && /^https?:\/\//i.test(href)) {
    return (
      <a href={href} className="ledger-register-row ledger-focus">
        {inner}
      </a>
    )
  }
  return href ? (
    <Link
      to={withLocalePath(href, locale)}
      className="ledger-register-row ledger-focus"
    >
      {inner}
    </Link>
  ) : (
    <div className="ledger-register-row">{inner}</div>
  )
}

/** A two-column specification table row. */
export function LedgerSpecRow({
  term,
  children,
}: {
  term: string
  children: ReactNode
}) {
  return (
    <div className="ledger-spec-row">
      <dt className="ledger-spec-key">{term}</dt>
      <dd className="ledger-spec-val">{children}</dd>
    </div>
  )
}

/** The document masthead line at the top of a page. */
export function LedgerMasthead({
  left,
  right,
}: {
  left: ReactNode
  right: ReactNode
}) {
  return (
    <div className="ledger-masthead">
      <span className="ledger-masthead-brand">{left}</span>
      <span>{right}</span>
    </div>
  )
}

/** The closing register notice. */
export function LedgerCta({
  title,
  body,
  ctaLabel,
  href = '/contact',
  topic,
}: {
  title: string
  body: string
  ctaLabel: string
  href?: string
  topic?: string
}) {
  const location = useLocation()
  const locale = getLocaleFromPath(location.pathname)
  return (
    <section className="ledger-cta">
      <div className="ledger-wrap flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <h2
            className="ledger-display text-[clamp(1.7rem,3.4vw,2.6rem)]"
            style={{ color: 'inherit' }}
          >
            {title}
          </h2>
          <p className="ledger-cta-sub mt-4 text-[1rem] leading-relaxed">
            {body}
          </p>
        </div>
        <Link
          to={withLocalePath(href, locale)}
          search={
            topic ? ({ topic } as any) : undefined
          }
          className="ledger-button ledger-focus shrink-0"
        >
          {ctaLabel}
        </Link>
      </div>
    </section>
  )
}

/** A status stamp, e.g. "In development". */
export function LedgerStamp({
  children,
  tone = 'stamp',
}: {
  children: ReactNode
  tone?: 'stamp' | 'ink'
}) {
  return (
    <span
      className={
        tone === 'stamp' ? 'ledger-stamp' : 'ledger-stamp ledger-stamp--ink'
      }
    >
      {children}
    </span>
  )
}

/**
 * A jurisdiction mark: the country's flag beside its name. The flag is the
 * record's issuing authority, so it is rendered as a small ruled plate, not a
 * decorative sticker.
 */
export function LedgerJurisdiction({
  code,
  name,
  note,
}: {
  code: 'de' | 'ch'
  name: string
  note?: string
}) {
  return (
    <div className="ledger-jurisdiction-cell">
      <img
        src={`/flags/${code}.svg`}
        alt=""
        aria-hidden="true"
        className="ledger-flag"
        width={28}
        height={20}
        loading="lazy"
        decoding="async"
      />
      <div className="ledger-jurisdiction-code mt-2.5">{name}</div>
      {note ? (
        <p className="ledger-muted mt-2 text-[0.8125rem]">{note}</p>
      ) : null}
    </div>
  )
}

/** Page header for interior pages. */
export function LedgerPageHeader({
  kicker,
  title,
  lead,
  status,
}: {
  kicker: string
  title: string
  lead: string
  status?: ReactNode
}) {
  return (
    <header className="ledger-wrap pb-10 pt-12 md:pt-16">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span className="ledger-kicker">{kicker}</span>
        {status}
      </div>
      <h1 className="ledger-display max-w-[20ch] text-[clamp(2.4rem,6vw,4.4rem)]">
        {title}
      </h1>
      <p className="ledger-lead mt-6">{lead}</p>
    </header>
  )
}
